import * as React from 'react';
import type { TreeItem, TreeItemIndex } from 'react-complex-tree';
import type { ITreeNode } from '../types';

// Synthetic root required by react-complex-tree. Does not show on render
const DEFAULT_ROOT_DATA: ITreeNode = { id: 'root', label: 'Root' };

const DEFAULT_ROOT_ITEM: TreeItem<ITreeNode> = {
	index: 'root',
	canMove: false,
	isFolder: false,
	children: [],
	data: DEFAULT_ROOT_DATA,
	canRename: false,
};

/**
 * Adapts domain ITreeNode tree nodes (external controlled type) to the explicit TreeItem<T> tree items map required by react-complex-tree.
 *
 * - Creates a synthetic container root (index "root") whose children are the top-level item ids (required by react-complex-tree)
 * - Recursively converts each `ITreeNode` to a `TreeItem<ITreeNode>` (the format needed for react-complex-tree)
 * - Applies limits: stops descending at `maxDepth` and caps number of children per node to `maxChildrenPerNode`
 */
export const useTreeItemsAdapter = (
	data: ITreeNode[] = [],
	maxDepth: number,
	maxChildrenPerNode: number
): Record<TreeItemIndex, TreeItem<ITreeNode>> => {
	return React.useMemo(() => {
		const items: Record<TreeItemIndex, TreeItem<ITreeNode>> = {};

		items.root = DEFAULT_ROOT_ITEM;

		const processNode = (node: ITreeNode, nodeDepth: number): TreeItemIndex => {
			let children: TreeItemIndex[] = [];

			let nodeHasChildren = false;
			if (node.children && nodeDepth < maxDepth) {
				const cappedChildNodes = node.children.slice(0, maxChildrenPerNode);
				children = cappedChildNodes.map(child => processNode(child, nodeDepth + 1));
				nodeHasChildren = Boolean(cappedChildNodes.length > 0);
			} else {
				nodeHasChildren = Boolean(node.children && node.children.length > 0);
			}

			items[node.id] = {
				index: node.id,
				data: node,
				isFolder: nodeHasChildren,
				children: children,
				canMove: false,
				canRename: false,
			};

			return node.id;
		};

		const processedNodes = data.map(node => processNode(node, 0));
		items.root.children = processedNodes;
		items.root.isFolder = Boolean(processedNodes.length > 0);

		return items;
	}, [data, maxDepth, maxChildrenPerNode]);
};
